/**
 * AKU v2 Promote Dialog — extracted from AKUV2Manager.
 *
 * Promotes MERGED proposals to AKU v2 entries with optional TRAINING_ONLY
 * forcing and inclusion of non-effective sources. Handles async polling
 * progress display via the `progressNode` slot.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Upload, RefreshCw, Play } from 'lucide-react';

const ClaimStatsPanel = ({ promoteExam, claimStats, onPrepareClaims, preparingClaims }) => {
  if (!claimStats) return null;
  const noMerged = claimStats.merged === 0
    && (claimStats.draft > 0 || claimStats.review > 0 || claimStats.approved > 0);

  return (
    <div className="p-3 bg-gray-50 rounded-lg space-y-2">
      <p className="font-medium text-sm">Claim Status für {promoteExam}:</p>
      <div className="grid grid-cols-4 gap-2 text-xs">
        <div className="text-center p-2 bg-white rounded border">
          <div className="text-lg font-bold text-gray-600">{claimStats.draft}</div>
          <div className="text-gray-500">DRAFT</div>
        </div>
        <div className="text-center p-2 bg-white rounded border">
          <div className="text-lg font-bold text-yellow-600">{claimStats.review}</div>
          <div className="text-gray-500">REVIEW</div>
        </div>
        <div className="text-center p-2 bg-white rounded border">
          <div className="text-lg font-bold text-blue-600">{claimStats.approved}</div>
          <div className="text-gray-500">APPROVED</div>
        </div>
        <div className="text-center p-2 bg-white rounded border">
          <div className="text-lg font-bold text-green-600">{claimStats.merged}</div>
          <div className="text-gray-500">MERGED</div>
        </div>
      </div>

      {noMerged && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
          <p className="text-yellow-800 font-medium">Keine MERGED Claims vorhanden!</p>
          <p className="text-yellow-700 mt-1">
            Claims müssen erst durch Submit → Approve → Merge Workflow.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 w-full border-yellow-400 text-yellow-800 hover:bg-yellow-100"
            onClick={onPrepareClaims}
            disabled={preparingClaims}
            data-testid="prepare-claims-btn"
          >
            {preparingClaims ? (
              <>
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                Claims werden vorbereitet...
              </>
            ) : (
              <>
                <Play className="w-3 h-3 mr-1" />
                Alle Claims vorbereiten (Submit→Approve→Merge)
              </>
            )}
          </Button>
        </div>
      )}

      {claimStats.merged > 0 && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
          <p className="text-green-800">
            ✓ {claimStats.merged} MERGED Claims bereit zur Promotion
          </p>
        </div>
      )}
    </div>
  );
};

const AkuPromoteDialog = ({
  open,
  onOpenChange,
  exams,
  promoteExam,
  onPromoteExamChange,
  claimStats,
  onPrepareClaims,
  preparingClaims,
  promoteForce,
  setPromoteForce,
  includeNonEffective,
  setIncludeNonEffective,
  promotionJob,
  isPolling,
  onPromoteAll,
  onClose,
  progressNode,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-blue-600" />
          MERGED Proposals zu AKU v2 promoten
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <Label>Exam Profile</Label>
          <Select value={promoteExam} onValueChange={onPromoteExamChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {exams.map((exam) => (
                <SelectItem key={exam} value={exam}>{exam}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <ClaimStatsPanel
          promoteExam={promoteExam}
          claimStats={claimStats}
          onPrepareClaims={onPrepareClaims}
          preparingClaims={preparingClaims}
        />

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="force-training"
            checked={promoteForce}
            onChange={(e) => setPromoteForce(e.target.checked)}
            className="rounded"
            data-testid="force-training-checkbox"
          />
          <Label htmlFor="force-training" className="cursor-pointer">
            Als TRAINING_ONLY forcieren
          </Label>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="include-non-effective"
            checked={includeNonEffective}
            onChange={(e) => setIncludeNonEffective(e.target.checked)}
            className="rounded"
            data-testid="include-non-effective-checkbox"
          />
          <Label htmlFor="include-non-effective" className="cursor-pointer">
            Nicht-effektive Quellen einschließen (als TRAINING_ONLY)
          </Label>
        </div>

        <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
          <p className="font-medium">Promotion Pipeline (Async):</p>
          <ul className="mt-1 list-disc list-inside text-xs space-y-1">
            <li>Alle MERGED Proposals für {promoteExam} werden konvertiert</li>
            <li>Batch-Dedupe Query Optimierung (IN-Abfrage)</li>
            <li>Bulk Insert in Batches (200-500)</li>
            <li>Echtzeit-Fortschrittsanzeige</li>
          </ul>
        </div>

        {progressNode}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {(promotionJob?.status === 'SUCCEEDED' || promotionJob?.status === 'FAILED')
            ? 'Schließen'
            : 'Abbrechen'}
        </Button>
        <Button
          onClick={onPromoteAll}
          disabled={isPolling}
          data-testid="promote-all-btn"
        >
          {isPolling ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Läuft...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Alle promoten
            </>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default AkuPromoteDialog;
